import { useState } from "react";
import { NavLink } from "react-router-dom";
import { ExternalLink, GitBranch, Pencil, RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Panel, StatusBadge } from "@/components/layout-2/ds";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DeployDialog } from "@/components/dialer/versions/deploy-dialog";
import { PickedVersion, PickVersionDialog } from "@/components/dialer/versions/pick-version-dialog";
import { DataService } from "@/services/data";
import { VersionStatus } from "@/types/dialer/graph-view";
import { DeployableWorkflow, InFlightPolicy } from "@/types/dialer/workflow";
import type { BadgeValue } from "@/types/dynamic-detail";
import { formatParis } from "@/utils/dialer-datetime";

/**
 * What `WorkflowSlotTab` serializes. Declared here rather than in `types/dialer` because
 * it is the shape of a tab payload and not of a dialer endpoint: it is read by exactly
 * this component.
 */
export type WorkflowSlotContent = {
    slot: string;
    slot_label: string;
    campaign_code: string;
    campaign_name: string;
    campaign_is_active: boolean;
    in_flight_policy: InFlightPolicy | null;
    can_deploy: boolean;
    deployment_console_url: string;
    version: {
        id: number;
        version: number;
        status: VersionStatus;
        /** `{label, tone}` of `status`, served by the backend. */
        status_badge?: BadgeValue;
        workflow_id: number;
        workflow_code: string;
        workflow_name: string;
        deployed_at: string | null;
        updated_at: string;
    } | null;
    workflow_url: string | null;
    editor_url: string | null;
    active_cards: number;
    has_deployable_version: boolean;
    empty_reason: string | null;
    error?: string;
};

export type WorkflowSlotPanelProps = {
    content: WorkflowSlotContent;
    /** Refetches the slot and record header after a deployment. */
    onMutate?: () => void;
};

/**
 * One campaign slot: what it runs and the actions available on it.
 *
 * The change is *not* implemented here. The button opens the same two dialogs the
 * deployment console opens — the version picker, then the audited deployment dialog that
 * shows the scenario guard, the comparison and the in-flight choice, and posts to
 * `POST /dialer/campaigns/<code>/deploy/`. There is one write path for a slot and this is
 * a second door onto it, not a second path: everything that decides whether a deployment
 * may happen stays in `dialer.services.deployments`.
 *
 * What this tab deliberately does not restate is the rest of the console — the readiness
 * verdict, the deployment history and the rollbacks. Those are about the campaign rather
 * than about one slot, and a copy of them here would be a copy to keep in step.
 */
export function WorkflowSlotPanel({ content, onMutate }: WorkflowSlotPanelProps) {
    const [picking, setPicking] = useState<"version" | "workflow" | null>(null);
    const [deployable, setDeployable] = useState<DeployableWorkflow[] | null>(null);
    const [deployableLoading, setDeployableLoading] = useState(false);
    const [deployableError, setDeployableError] = useState<string | null>(null);
    const [picked, setPicked] = useState<PickedVersion | null>(null);

    if (content.error) {
        return (
            <Alert variant="destructive" className="mt-4">
                <AlertTitle>This tab is misconfigured</AlertTitle>
                <AlertDescription>{content.error}</AlertDescription>
            </Alert>
        );
    }

    // Loaded on the click rather than with the tab: it is a question about every other
    // workflow, and it is only asked by somebody about to change this slot.
    const loadDeployable = async () => {
        setDeployableLoading(true);
        setDeployableError(null);
        try {
            const response = (await DataService.client.url(`/dialer/campaigns/${content.campaign_code}/deployable-versions/`).get().json()) as DeployableWorkflow[];
            setDeployable(Array.isArray(response) ? response : []);
        } catch {
            setDeployableError("Check your connection and try again. No deployment has been started.");
        } finally {
            setDeployableLoading(false);
        }
    };

    const openPicker = (mode: "version" | "workflow") => {
        setPicking(mode);
        if (deployable === null && !deployableLoading) void loadDeployable();
    };

    const version = content.version;

    return (
        <div className="mt-2">
            <Panel>
                <div className="flex items-center gap-2">
                    <GitBranch className="h-4 w-4 flex-shrink-0 text-gray-500" />
                    <h2 className="min-w-0 truncate text-base font-bold">{content.slot_label} workflow</h2>
                    {version ? (
                        <StatusBadge value={version.status_badge} className="ml-auto" />
                    ) : (
                        <Badge tone="neutral" className="ml-auto">
                            Empty
                        </Badge>
                    )}
                </div>
                {version ? (
                    <div className="mt-2 min-w-0 border-t pt-2">
                        <div className="flex min-w-0 items-baseline gap-2">
                            <p className="truncate text-sm text-black">{version.workflow_name || version.workflow_code}</p>
                            <span className="shrink-0 font-mono text-xs text-gray-500">v{version.version}</span>
                        </div>
                        <p className="mt-0.5 text-xs text-gray-500">
                            {version.deployed_at ? "Deployed" : "Updated"} {formatParis(version.deployed_at ?? version.updated_at)}
                        </p>
                    </div>
                ) : (
                    <p className="mt-2 border-t pt-2 text-sm text-gray-500">No workflow deployed.</p>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                    {content.workflow_url && (
                        <Button variant="outline" size="sm" className="bg-white" asChild>
                            <NavLink to={content.workflow_url}>
                                <ExternalLink className="mr-1 h-4 w-4" /> View workflow
                            </NavLink>
                        </Button>
                    )}
                    {content.can_deploy && content.editor_url && (
                        <Button variant="outline" size="sm" className="bg-white" asChild>
                            <NavLink to={content.editor_url}>
                                <Pencil className="mr-1 h-4 w-4" /> Edit on canvas
                            </NavLink>
                        </Button>
                    )}
                    {content.can_deploy && (
                        <Button variant="outline" size="sm" className="bg-white" onClick={() => openPicker(version ? "version" : "workflow")}>
                            <RefreshCw className="mr-1 h-4 w-4" /> {version ? "Change version" : "Choose workflow"}
                        </Button>
                    )}
                    {content.can_deploy && version && (
                        <Button variant="ghost" size="sm" className="text-gray-600" onClick={() => openPicker("workflow")}>
                            Switch to another workflow
                        </Button>
                    )}
                </div>
            </Panel>

            {picking && (
                <PickVersionDialog
                    open
                    onOpenChange={open => !open && setPicking(null)}
                    slot={content.slot}
                    workflows={deployable}
                    loading={deployableLoading}
                    error={deployableError}
                    currentVersionId={content.version?.id}
                    workflowCode={picking === "version" ? version?.workflow_code : undefined}
                    workflowName={picking === "version" ? version?.workflow_name : undefined}
                    onSwitchWorkflow={picking === "version" ? () => setPicking("workflow") : undefined}
                    onRetry={() => void loadDeployable()}
                    onPick={next => {
                        setPicking(null);
                        setPicked(next);
                    }}
                />
            )}
            {picked && (
                <DeployDialog
                    open
                    onOpenChange={open => !open && setPicked(null)}
                    workflowCode={picked.workflowCode}
                    workflowName={picked.workflowName}
                    versionId={picked.versionId}
                    versionNumber={picked.versionNumber}
                    targets={[{ campaign_code: content.campaign_code, campaign_name: content.campaign_name, slot: content.slot }]}
                    onDone={() => {
                        setPicked(null);
                        onMutate?.();
                    }}
                />
            )}
        </div>
    );
}
